import palette from '@assets/theme/palette';
import { GTagWrapper } from '@components/GTagWrapper/GTagWrapper';
import { BitrixDisableUserGuard } from '@components/guards/bitrixUserGuard/BitrixUserGuard';
import EditIcon from '@components/icons/Edit';
import SearchIcon from '@components/icons/SearchIcon';
import { YaTargetWrapper } from '@components/YaTargetWrapper/YaTargetWrapper';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CloseIcon from '@material-ui/icons/Close';
import LinkIcon from '@mui/icons-material/Link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip'; // Компонент виджета расчета маршрутов
import ContainerParametersComponent from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/containerCargo/ContainerParametersComponent';
import CustomCargoParametersComponent from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/customCargo/CustomCargoParametersComponent';
import { CargoType } from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/interfaces';
import VideoInstruction from '@pages/AdditionalServices/tabs/DeliveryForm/components/RoutesCalculationWidget/VideoInstruction';
import { useStateSubscriber } from '@settings/core/stateContexts/useContextSubscriber';
import clsx from 'clsx';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BehaviorSubject } from 'rxjs';
import { onCopyToClipboard } from 'src/utils/browser-permission-checks';

import { checkEmptyInputValue } from '../../helpers';
import useDelivery from '../../useDelivery';
import CalculationLimit from './CalculationLimit';
import CargoDate from './CargoDate';
import CargoTypeSelect from './CargoTypeSelect';
import Locations from './Locations';
import RouteParameters from './RouteParameters';
import { ShareMenu } from './ShareMenu';
import RoutesCalculationWidgetStyledContainer from './StyledComponents';
import TransportWithCustomWeightDiffDialog from './TransportWithCustomWeightDiffDialog';
import TruckParametersComponent from './truckCargo/TruckParametersComponent';

interface RoutesCalculationWidgetProps {
  handleShowFormButton: () => void;
  openDrawer: boolean;
}

export const $isCalculationLimitExceeded = new BehaviorSubject<boolean>(false);

// Компонент виджета расчета маршрутов
const RoutesCalculationWidget: FC<RoutesCalculationWidgetProps> = ({ handleShowFormButton }) => {
  const [isLimitExceeded] = useStateSubscriber<boolean>($isCalculationLimitExceeded).useState();

  const {
    useBaseDate,
    useRouteParams,
    useValidation,
    useSettings,
    useIsLoading,
    useIsNeedReloading,
    useIsArchiveLoading,
    useIsEditCalculationParametersActive,
    useIsRoutesLoading,
    actions,
  } = useDelivery();
  const theme = useTheme();
  const setArchiveMode = useIsArchiveLoading().setState;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation([
    'RoutesCalculationWidget',
    'Actions',
    'LocationSelector',
    'Common',
  ]);
  const [baseData] = useBaseDate().useState();
  const [error] = useValidation().useState();
  const [routeParams, setRouteParameter] = useRouteParams().useState();
  const [settings] = useSettings().useState();
  const [isLoading] = useIsLoading().useState();
  const [isRoutesLoading] = useIsRoutesLoading().useState();
  const [isNeedRoutesReloading] = useIsNeedReloading().useState(false);
  const [isEditCalculationParametersActive] = useIsEditCalculationParametersActive().useState();

  const [isCargoFieldsFilled, setIsCargoFieldsFilled] = useState(true);
  const [copyNotificationVisible, setCopyNotificationVisible] = useState(false);

  const {
    resetWidget,
    saveToSearchParams,
    resetSearchParams,
    createAndRunRouteSearchTask,
    abortRouteSearch,
  } = actions;
  const { startExpeditionDate, startLocation, endLocation } = routeParams;

  const isContainer = useMemo(
    () => routeParams.cargoType && ['container'].indexOf(routeParams.cargoType) !== -1,
    [routeParams.cargoType]
  );
  const isTruck = useMemo(
    () => routeParams.cargoType && ['auto'].indexOf(routeParams.cargoType) !== -1,
    [routeParams.cargoType]
  );
  const isCustomCargo = useMemo(
    () =>
      routeParams.cargoType && ['custom', 'air', 'fclFtl'].indexOf(routeParams.cargoType) !== -1,
    [routeParams.cargoType]
  );
  const isFclFtl = useMemo(
    () => routeParams.cargoType && 'fclFtl' === routeParams.cargoType,
    [routeParams.cargoType]
  );

  const maybeCalculated = useMemo(
    () => !!startExpeditionDate && !!startLocation && !!endLocation && !!routeParams.cargoType,
    [startExpeditionDate, startLocation, endLocation, routeParams.cargoType]
  );

  const isStartTerminalNotSelected = !!startLocation && startLocation.type !== 'terminal';
  const isStartWarehouseNotSelected =
    !!startLocation && startLocation.type === 'location' && !startLocation.isWarehouse;
  const isStartCarrierTerminalNotSelected =
    !!startLocation && startLocation.type === 'location' && startLocation.symbolCode !== 'ANY';

  const isEndTerminalNotSelected = !!endLocation && endLocation.type !== 'terminal';
  const isEndWarehouseNotSelected =
    !!endLocation && endLocation.type === 'location' && !endLocation.isWarehouse;
  const isEndCarrierTerminalNotSelected =
    !!endLocation && endLocation.type === 'location' && endLocation.symbolCode !== 'ANY';

  const isStartNotSelected =
    !startLocation ||
    (isStartTerminalNotSelected &&
      isStartWarehouseNotSelected &&
      isStartCarrierTerminalNotSelected);
  const isEndNotSelected =
    !endLocation ||
    (isEndTerminalNotSelected && isEndWarehouseNotSelected && isEndCarrierTerminalNotSelected);

  const isFclFtlTransportTypesFilled = useMemo(
    () =>
      routeParams.customCargoParameters.transportTypes.some((t) => t.type === 'container') &&
      routeParams.customCargoParameters.transportTypes.some((t) => t.type === 'truck') &&
      routeParams.customCargoParameters.transportTypes.every(
        (t) => !checkEmptyInputValue(t.weight)
      ),
    [routeParams.customCargoParameters.transportTypes]
  );

  useEffect(() => {
    const isFormInvalid =
      !routeParams.cargoType || isStartNotSelected || isEndNotSelected || !isCargoFieldsFilled;

    const timerId = setTimeout(() => {
      if (!isFormInvalid) {
        saveToSearchParams();
      } else {
        resetSearchParams();
      }
    }, 500);

    return () => clearTimeout(timerId);
  });

  const handleDeleteContainerParameters = (index: number) => {
    setRouteParameter((current) => ({
      ...current,
      containerParameters: {
        ...current.containerParameters!,
        containerParameters: current.containerParameters!.containerParameters.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };
  const handleDeleteTruckParameters = (index: number) => {
    setRouteParameter((current) => ({
      ...current,
      truckParameters: {
        ...current.truckParameters!,
        truckParameters: current.truckParameters!.truckParameters.filter((_, i) => i !== index),
      },
    }));
  };
  const handleDeleteCustomParameters = (index: number) => {
    setRouteParameter((current) => ({
      ...current,
      customCargoParameters: {
        ...current.customCargoParameters!,
        transportTypes: [],
        customCargoParameters: current.customCargoParameters!.customCargoParameters.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleSearchButton = () => {
    if (isRoutesLoading || isLoading) {
      return;
    }

    setArchiveMode(false);
    createAndRunRouteSearchTask(true);

    if (isMobile) {
      handleShowFormButton();
    }
  };

  const onChangeCargoType = (cargoType: CargoType) => {
    let requiredTransportingTypes = [];
    switch (cargoType) {
      case 'air':
        requiredTransportingTypes = [3];
        break;
      case 'auto':
        requiredTransportingTypes = [4];
        break;
      default:
        requiredTransportingTypes = [];
        break;
    }

    setRouteParameter((current) => ({
      ...current,
      cargoType,
      requiredTransportingTypes,
    }));
  };

  const changeCargoFieldsFilledHandler = (validityResults: boolean[]) => {
    const isFieldsValid = validityResults.every((value) => !value);

    setIsCargoFieldsFilled(isFieldsValid);
  };

  const copyCalculationLinkHandler = () => {
    onCopyToClipboard(window.location.href, () => {
      setCopyNotificationVisible(true);
      setTimeout(() => {
        setCopyNotificationVisible(false);
      }, 2000);
    });
  };

  return (
    <>
      <RoutesCalculationWidgetStyledContainer>
        <div className={'form-wrapper'}>
          <div className={'form-wrapper__fields'}>
            <Locations
              BaseData={baseData}
              startTransportingCondition={routeParams.startTransportingCondition}
              endTransportingCondition={routeParams.endTransportingCondition}
              startLocation={routeParams.startLocation}
              endLocation={routeParams.endLocation}
              onSwapLocation={() => {
                setRouteParameter((current) => ({
                  ...current,
                  startLocation: current.endLocation,
                  endLocation: current.startLocation,
                  startTransportingCondition: current.endTransportingCondition,
                  endTransportingCondition: current.startTransportingCondition,
                }));
              }}
              onResetEndLocation={() => {
                const defaultEndTransportingCondition = baseData.endTransportingConditions.find(
                  (condition) => condition.id === '1'
                );
                setRouteParameter((current) => ({
                  ...current,
                  endLocation: undefined,
                  endTransportingCondition: defaultEndTransportingCondition.id,
                }));
              }}
              onResetStartLocation={() => {
                const defaultStartTransportingCondition = baseData.startTransportingConditions.find(
                  (condition) => condition.id === '1'
                );
                setRouteParameter((current) => ({
                  ...current,
                  startLocation: undefined,
                  startTransportingCondition: defaultStartTransportingCondition.id,
                }));
              }}
              onChangeEndLocation={(endLocation) => {
                setRouteParameter((current) => ({
                  ...current,
                  endLocation,
                }));
              }}
              onChangeStartLocation={(startLocation) => {
                setRouteParameter((current) => ({
                  ...current,
                  startLocation,
                }));
              }}
              onChangeEndTransportingCondition={(endTransportingCondition, checked) => {
                setRouteParameter((current) => ({
                  ...current,
                  endTransportingCondition,
                  isEndLoadingIncluded: checked,
                }));
              }}
              onChangeStartTransportingCondition={(startTransportingCondition, checked) => {
                setRouteParameter((current) => ({
                  ...current,
                  startTransportingCondition,
                  isStartLoadingIncluded: checked,
                }));
              }}
              secondaryLangId={settings.secondaryLanguageId}
              primaryLangId={settings.primaryLanguageId}
            />
            <CargoDate
              currentDate={routeParams.startExpeditionDate}
              onChangeDate={(startExpeditionDate) => {
                setRouteParameter((current) => ({
                  ...current,
                  startExpeditionDate,
                }));
              }}
            />
            <CargoTypeSelect
              cargoType={routeParams.cargoType}
              onChangeCargoType={onChangeCargoType}
            />
          </div>
          {!isMobile && (
            <GTagWrapper target="calculator_poisk">
              <YaTargetWrapper target="calculator_poisk">
                <Fab
                  color={'primary'}
                  className={'form-wrapper__search'}
                  onClick={handleSearchButton}
                  size={'medium'}
                  disabled={
                    !routeParams.cargoType ||
                    isStartNotSelected ||
                    isEndNotSelected ||
                    !isCargoFieldsFilled ||
                    isRoutesLoading ||
                    isLoading ||
                    (isFclFtl && !isFclFtlTransportTypesFilled)
                  }
                >
                  <SearchIcon
                    sx={{
                      fontSize: 25,
                      color: 'common.white',
                    }}
                  />
                </Fab>
              </YaTargetWrapper>
            </GTagWrapper>
          )}
          {!isMobile && (
            <>
              {!error && <div className={'error-massage'}>Пожалуйста, заполните все поля</div>}
              {!routeParams.cargoType && (
                <Box className={'video-instruction-block'}>
                  <VideoInstruction />
                </Box>
              )}
            </>
          )}
        </div>
        {!!routeParams.cargoType && (
          <>
            {isTruck && !!routeParams.truckParameters && (
              <TruckParametersComponent
                BaseData={baseData}
                truckParameters={routeParams.truckParameters}
                mainLanguage={settings.secondaryLanguageId}
                systemLanguage={settings.primaryLanguageId}
                copyNotificationVisible={copyNotificationVisible}
                handleDeleteTruckParameters={handleDeleteTruckParameters}
                onChangeCargoFieldsFilled={changeCargoFieldsFilledHandler}
                baseConditionsSlot={<RouteParameters />}
                resetSlot={
                  !isMobile && (
                    <>
                      <BitrixDisableUserGuard>
                        <Tooltip title={t('RoutesCalculationWidget:CopyCalculationLink')}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={copyCalculationLinkHandler}
                          >
                            <LinkIcon
                              sx={{ transform: 'rotate(-45deg)' }}
                              style={{ color: palette.light.grey[700] }}
                            />
                          </IconButton>
                        </Tooltip>
                      </BitrixDisableUserGuard>
                      <ShareMenu isShareNotAllowed={false} />
                      {isEditCalculationParametersActive && (
                        <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                          <Tooltip title={t('RoutesCalculationWidget:EditCalculationOptions')}>
                            <IconButton color="primary" size="small" onClick={abortRouteSearch}>
                              <EditIcon style={{ color: palette.light.grey[700] }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                      <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                        <Tooltip title={t('RoutesCalculationWidget:ResetCalculationOptions')}>
                          <IconButton color="primary" size="small" onClick={resetWidget}>
                            <CloseIcon style={{ color: palette.light.error.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </>
                  )
                }
              />
            )}
            {isContainer && !!routeParams.containerParameters && (
              <ContainerParametersComponent
                BaseData={baseData}
                containerParameters={routeParams.containerParameters}
                mainLanguage={settings.secondaryLanguageId}
                systemLanguage={settings.primaryLanguageId}
                copyNotificationVisible={copyNotificationVisible}
                handleDeleteContainerParameters={handleDeleteContainerParameters}
                onChangeCargoFieldsFilled={changeCargoFieldsFilledHandler}
                baseConditionsSlot={<RouteParameters />}
                resetSlot={
                  !isMobile && (
                    <>
                      <BitrixDisableUserGuard>
                        <Tooltip title={t('RoutesCalculationWidget:CopyCalculationLink')}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={copyCalculationLinkHandler}
                          >
                            <LinkIcon
                              sx={{ transform: 'rotate(-45deg)' }}
                              style={{ color: palette.light.grey[700] }}
                            />
                          </IconButton>
                        </Tooltip>
                      </BitrixDisableUserGuard>
                      <ShareMenu isShareNotAllowed={false} />
                      {isEditCalculationParametersActive && (
                        <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                          <Tooltip title={t('RoutesCalculationWidget:EditCalculationOptions')}>
                            <IconButton color="primary" size="small" onClick={abortRouteSearch}>
                              <EditIcon style={{ color: palette.light.grey[700] }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                      <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                        <Tooltip title={t('RoutesCalculationWidget:ResetCalculationOptions')}>
                          <IconButton color="primary" size="small" onClick={resetWidget}>
                            <CloseIcon style={{ color: palette.light.error.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </>
                  )
                }
              />
            )}
            {isCustomCargo && !!routeParams.customCargoParameters && (
              <CustomCargoParametersComponent
                BaseData={baseData}
                customCargoParameters={routeParams.customCargoParameters}
                mainLanguage={settings.primaryLanguageId}
                systemLanguage={settings.secondaryLanguageId}
                copyNotificationVisible={copyNotificationVisible}
                handleDeleteCustomParameters={handleDeleteCustomParameters}
                onChangeCargoFieldsFilled={changeCargoFieldsFilledHandler}
                baseConditionsSlot={<RouteParameters />}
                resetSlot={
                  !isMobile && (
                    <>
                      <BitrixDisableUserGuard>
                        <Tooltip title={t('RoutesCalculationWidget:CopyCalculationLink')}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={copyCalculationLinkHandler}
                          >
                            <LinkIcon
                              sx={{ transform: 'rotate(-45deg)' }}
                              style={{ color: palette.light.grey[700] }}
                            />
                          </IconButton>
                        </Tooltip>
                      </BitrixDisableUserGuard>
                      <ShareMenu isShareNotAllowed={false} />
                      {isEditCalculationParametersActive && (
                        <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                          <Tooltip title={t('RoutesCalculationWidget:EditCalculationOptions')}>
                            <IconButton color="primary" size="small" onClick={abortRouteSearch}>
                              <EditIcon style={{ color: palette.light.grey[700] }} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                      <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                        <Tooltip title={t('RoutesCalculationWidget:ResetCalculationOptions')}>
                          <IconButton color="primary" size="small" onClick={resetWidget}>
                            <CloseIcon style={{ color: palette.light.error.main }} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </>
                  )
                }
              />
            )}
          </>
        )}
        {isMobile && (
          <Box
            sx={{
              mb: 1,
            }}
          >
            <div className={'wrapper-actions-form'}>
              <Button
                disabled={
                  isNeedRoutesReloading ||
                  !maybeCalculated ||
                  isRoutesLoading ||
                  isLoading ||
                  (isFclFtl && !isFclFtlTransportTypesFilled)
                }
                variant={'contained'}
                fullWidth
                size={'large'}
                onClick={handleSearchButton}
                startIcon={<SearchIcon sx={{ color: 'white' }} />}
                className={'search-button'}
              >
                {t('Actions:Search')}
              </Button>
            </div>
            <div className="share-wrapper">
              <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                <Tooltip title={t('RoutesCalculationWidget:ResetCalculationOptions')}>
                  <IconButton color="primary" size="small" onClick={resetWidget}>
                    <CloseIcon style={{ color: palette.light.error.main }} />
                  </IconButton>
                </Tooltip>
              </div>
              {isEditCalculationParametersActive && (
                <div className={clsx({ 'accent-wrapper': isRoutesLoading || isLoading })}>
                  <Tooltip title={t('RoutesCalculationWidget:EditCalculationOptions')}>
                    <IconButton color="primary" size="small" onClick={abortRouteSearch}>
                      <EditIcon style={{ color: palette.light.grey[700] }} />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              <BitrixDisableUserGuard>
                <Tooltip title={t('RoutesCalculationWidget:CopyCalculationLink')}>
                  <IconButton color="primary" size="small" onClick={copyCalculationLinkHandler}>
                    <LinkIcon
                      sx={{ transform: 'rotate(-45deg)', fontSize: '33px' }}
                      style={{ color: palette.light.grey[700] }}
                    />
                  </IconButton>
                </Tooltip>
              </BitrixDisableUserGuard>
              <ShareMenu isShareNotAllowed={false} />
            </div>
            {!error && <div className={'error-massage'}>Пожалуйста, заполните все поля</div>}
            <VideoInstruction />
          </Box>
        )}
      </RoutesCalculationWidgetStyledContainer>
      <CalculationLimit isVisible={isLimitExceeded} />
      <TransportWithCustomWeightDiffDialog />
    </>
  );
};

// Экспортируем компонент
export default RoutesCalculationWidget;
